import { DateTime } from 'luxon'
import { computed, ref, Ref } from 'vue'
import { fromISOWithZone, getDateStringForAPI } from '@/libs/date/date'
import { setupDateTimeUtils } from '@/pages/PatientProfile/shared/dateAndTimezones'

export const DEFAULT_END_TIME_OFFSET_MINUTES = 120

/**
 * A start time requires an end time, so save paths that send a dueDatetime
 * without an endDatetime use this to fill it in.
 * @param dueDatetime the call's start datetime (ISO string)
 * @returns the default end datetime, or undefined if there is no dueDatetime
 */
export function getDefaultEndDatetime(
  dueDatetime: string | null | undefined
): string | undefined {
  if (!dueDatetime) return undefined
  const end = fromISOWithZone(dueDatetime).plus({
    minutes: DEFAULT_END_TIME_OFFSET_MINUTES,
  })
  return getDateStringForAPI(end.toJSDate()) ?? undefined
}

/**
 * The end datetime after the start date changes: moves the end by the same
 * number of calendar days as the start, so a call that ends after midnight
 * still ends after midnight on the new date.
 * @param endDatetime the call's current end datetime (ISO string)
 * @param previousDueDatetime the call's start datetime before the date change (ISO string)
 * @param newStartDate the newly selected start date
 * @returns the shifted end datetime, or endDatetime unchanged if there is no previous start
 */
export function getShiftedEndDatetime(
  endDatetime: string,
  previousDueDatetime: string | null | undefined,
  newStartDate: Date
): string {
  if (!previousDueDatetime) return endDatetime

  const previousStartDay = fromISOWithZone(previousDueDatetime)
    .toLocal()
    .startOf('day')
  const newStartDay = DateTime.fromJSDate(newStartDate).startOf('day')
  const days = Math.round(newStartDay.diff(previousStartDay, 'days').days)

  const shiftedEnd = fromISOWithZone(endDatetime).toLocal().plus({ days })
  return getDateStringForAPI(shiftedEnd.toJSDate()) ?? endDatetime
}

/**
 * Shared end-time logic for the "Scheduled call end time" field
 * Owns only the end-time bounds/anchoring rule
 * so it can't drift between callers - start-time selection (date/time
 * picker handling) stays bespoke per caller.
 * @param dueDatetime caller's current start datetime (ISO string), read-only value used to anchor its bounds
 * @param endDatetime caller's own end datetime storage (ISO string), writable
 */
export function useScheduledCallEndTime(
  dueDatetime: Ref<string | null | undefined>,
  endDatetime: Ref<string | null | undefined>
) {
  const { stringToLocalDateTime, CURRENT_USER_TZ } = setupDateTimeUtils()

  const dirtyEndDateTime = computed<Date | null>({
    get: () => stringToLocalDateTime(endDatetime.value)?.toJSDate() ?? null,
    set: (value) => setEndDatetime(value),
  })

  const startTimeAsDate = computed(
    () => stringToLocalDateTime(dueDatetime.value)?.toJSDate() ?? null
  )

  // Bumped when an out-of-range end time is rejected so callers can use it in
  // the picker's :key to force a remount back to the model value.
  const endTimePickerKey = ref(0)

  const endTimeMinDate = computed(() => startTimeAsDate.value ?? undefined)

  const endTimeMaxDate = computed(() =>
    startTimeAsDate.value
      ? DateTime.fromJSDate(startTimeAsDate.value).plus({ hours: 5 }).toJSDate()
      : undefined
  )

  /**
   * Update the end time. Always shares dueDatetime's date - only the
   * time-of-day changes.
   *
   * Assumes the call does not cross a date boundary in the local (browser)
   * timezone - only hour/minute are updated, so a call that spans midnight
   * local time would not roll over to the next day.
   * @param value Time to set from, or null to clear. Clearing is rejected while a
   * start time is set, since a start time requires an end time.
   */
  function setEndDatetime(value: Date | null) {
    if (!dueDatetime.value) {
      if (!value) endDatetime.value = undefined
      return
    }

    if (!value) {
      // e.g. the user deleted the picker's text; remount it back to the model value
      endTimePickerKey.value += 1
      return
    }

    const baseDateTime = endDatetime.value
      ? fromISOWithZone(endDatetime.value).toLocal()
      : fromISOWithZone(dueDatetime.value).toLocal()

    const newEndDateTime = baseDateTime.set({
      hour: value.getHours(),
      minute: value.getMinutes(),
    })

    // PrimeVue's minute up-arrow validates the unwrapped minute (e.g. 4:60) but
    // stores the wrapped one (4:00) without carrying the hour, letting the picker
    // slip below min-date. Enforce the lower bound here and remount the picker so
    // it re-syncs to the unchanged model value.
    if (
      startTimeAsDate.value &&
      newEndDateTime.toMillis() < startTimeAsDate.value.getTime()
    ) {
      endTimePickerKey.value += 1
      return
    }

    endDatetime.value =
      getDateStringForAPI(newEndDateTime.toJSDate()) ?? undefined
  }

  /**
   * Reset end time to a fixed offset after a newly-selected start time,
   * overwriting any previously set end time.
   * @param newStartDateTime the just-selected start datetime
   * @param offsetMinutes minutes after start to default the end time to
   */
  function resetEndTimeToDefaultOffset(
    newStartDateTime: DateTime,
    offsetMinutes = DEFAULT_END_TIME_OFFSET_MINUTES
  ) {
    const newEndDateTime = newStartDateTime.plus({ minutes: offsetMinutes })
    endDatetime.value =
      getDateStringForAPI(newEndDateTime.toJSDate()) ?? undefined
  }

  /**
   * Clear the end time, e.g. when the start time is cleared.
   */
  function clearEndTime() {
    endDatetime.value = undefined
  }

  /**
   * Move the end time along with a start date change. No-op if there is no end time.
   * Call this before the caller overwrites its dueDatetime, since the shift is
   * relative to the previous start date.
   * @param previousDueDatetime the start datetime before the date change (ISO string)
   * @param newStartDate the newly selected start date
   */
  function shiftEndDatetimeToDate(
    previousDueDatetime: string | null | undefined,
    newStartDate: Date
  ) {
    if (!endDatetime.value) return
    endDatetime.value = getShiftedEndDatetime(
      endDatetime.value,
      previousDueDatetime,
      newStartDate
    )
  }

  const scheduledCallEndTimeLabel = `Scheduled call end time (Your time: ${CURRENT_USER_TZ})`

  return {
    dirtyEndDateTime,
    endTimeMinDate,
    endTimeMaxDate,
    endTimePickerKey,
    setEndDatetime,
    resetEndTimeToDefaultOffset,
    clearEndTime,
    shiftEndDatetimeToDate,
    scheduledCallEndTimeLabel,
  }
}
